import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { translateMessage, translateMessageAsync } from '../utils/chatTranslation';
import '../styles/LobbyChat.css';
import logger from '../utils/logger';

// Helper function to sanitize message display - hide image/binary data
const sanitizeMessageDisplay = (message) => {
  if (!message) return '';
  
  // Check for base64 image data patterns
  if (message.startsWith('data:image') || 
      message.startsWith('data:audio') ||
      message.startsWith('data:video') ||
      message.match(/^[A-Za-z0-9+/=]{50,}$/)) {
    return '📎 [Media attachment]';
  }
  
  // Check for very long strings of random characters (likely encoded data)
  if (message.length > 100 && !message.includes(' ') && /^[A-Za-z0-9+/=_-]+$/.test(message)) {
    return '📎 [Attachment]';
  }
  
  return message;
};

const LobbyChat = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [onlineCount, setOnlineCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [asyncTranslations, setAsyncTranslations] = useState({});
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const { user, token } = useAuth();
  const { language } = useTranslation();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const WS_URL = API_URL.replace('http://', 'ws://').replace('https://', 'wss://');

  // Fetch AI translation for a message
  const fetchTranslation = useCallback(async (msgKey, text) => {
    if (!text || asyncTranslations[msgKey]) return;
    
    try {
      const result = await translateMessageAsync(text, language);
      if (result.translated) {
        setAsyncTranslations(prev => ({
          ...prev,
          [msgKey]: result.translated
        }));
      }
    } catch (err) {
      logger.error('Translation fetch error:', err);
    }
  }, [language, asyncTranslations]);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Connect to lobby WebSocket
  const connectLobbyChat = () => {
    if (!user || !user.user_id || user.is_guest) return;

    try {
      const ws = new WebSocket(`${WS_URL}/api/ws/lobby`);
      
      ws.onopen = () => {
        setWsConnected(true);
        // Join lobby
        ws.send(JSON.stringify({
          type: 'join_lobby',
          user_id: user.user_id,
          user_name: user.name
        }));
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'lobby_message') {
          // Add new message
          setMessages(prev => {
            const newMessages = [...prev, {
              user_id: data.user_id,
              user_name: data.user_name,
              message: data.message,
              timestamp: data.timestamp
            }];
            
            // Keep only last 100 messages (FIFO - First In First Out)
            if (newMessages.length > 100) {
              return newMessages.slice(-100);
            }
            return newMessages;
          });
        } else if (data.type === 'online_count') {
          setOnlineCount(data.count);
        } else if (data.type === 'online_users') {
          // Update online users list
          setOnlineUsers(data.users || []);
        } else if (data.type === 'user_joined') {
          // Add user to online list
          const userName = data.user_name;
          setOnlineUsers(prev => {
            if (!prev.find(u => u.user_name === userName)) {
              return [...prev, { user_name: userName, user_id: data.user_id }];
            }
            return prev;
          });
        } else if (data.type === 'user_left') {
          // Remove user from online list
          const userName = data.user_name;
          setOnlineUsers(prev => prev.filter(u => u.user_name !== userName));
        }
      };

      ws.onerror = (error) => {
        logger.error('Lobby chat WebSocket error:', error);
      };

      ws.onclose = () => {
        setWsConnected(false);
        // Attempt to reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isOpen && user && !user.is_guest) {
            connectLobbyChat();
          }
        }, 3000);
      };

      wsRef.current = ws;
    } catch (error) {
      logger.error('Failed to connect lobby chat:', error);
    }
  };

  // Disconnect WebSocket
  const disconnectLobbyChat = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      // Send leave message before closing
      try {
        wsRef.current.send(JSON.stringify({
          type: 'leave_lobby'
        }));
      } catch (e) {
        logger.error('Error sending leave message:', e);
      }
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  // Send message
  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!messageInput.trim() || !wsRef.current || sending || !wsConnected) {
      if (!wsConnected) {
        alert('Not connected to chat. Please wait...');
      }
      return;
    }

    try {
      setSending(true);
      wsRef.current.send(JSON.stringify({
        type: 'lobby_message',
        user_id: user.user_id,
        user_name: user.name,
        message: messageInput.trim()
      }));

      setMessageInput('');
    } catch (error) {
      logger.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Connect on mount
  useEffect(() => {
    if (isOpen && token && user && !user.is_guest) {
      connectLobbyChat();
    }
    
    // Cleanup on unmount or close
    return () => {
      disconnectLobbyChat();
      setMessages([]); // Clear messages when closing
    };
  }, [isOpen, token, user?.user_id]);

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  // Show message for guest users
  if (user?.is_guest) {
    return (
      <div className="lobby-chat-overlay" onClick={onClose}>
        <div className="lobby-chat-container" onClick={(e) => e.stopPropagation()}>
          <div className="lobby-chat-header">
            <h2>💬 Live Chat</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="guest-message">
            <p className="guest-icon">🔒</p>
            <p className="guest-title">Registered Users Only</p>
            <p className="guest-subtitle">Please create an account to join the live chat!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lobby-chat-overlay" onClick={onClose}>
      <div className="lobby-chat-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="lobby-chat-header">
          <h2>💬 Live Chat</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Main Content with Sidebar */}
        <div className="lobby-chat-content">
          {/* Online Users Sidebar */}
          <div className="online-users-sidebar">
            <div className="sidebar-header">
              <span className="online-indicator">🟢</span>
              <span>Online ({onlineCount})</span>
            </div>
            <div className="online-users-list">
              {onlineUsers.length === 0 ? (
                <div className="no-users">No one online yet</div>
              ) : (
                onlineUsers.map((u) => (
                  <div key={u.user_id} className="online-user-item">
                    <div className="user-avatar-with-status">
                      <span className="user-avatar">👤</span>
                      <span className="online-status-dot"></span>
                    </div>
                    <span className="user-name">{u.user_name}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Messages Area */}
          <div className="lobby-messages-wrapper">
            <div className="lobby-messages">
          {messages.length === 0 ? (
            <div className="empty-chat">
              <p className="empty-icon">💬</p>
              <p className="empty-title">No messages yet</p>
              <p className="empty-subtitle">Be the first to say hello!</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const isOnline = onlineUsers.some(u => u.user_name === msg.user_name);
              const msgKey = `${msg.timestamp}-${index}`;
              const isOwnMessage = msg.user_id === user?.user_id;
              
              // First try phrase dictionary translation
              const phraseTranslation = !isOwnMessage 
                ? translateMessage(msg.message, language)
                : { original: msg.message, translated: null };
              
              // Check if we have an async AI translation
              const aiTranslation = asyncTranslations[msgKey];
              
              // Use phrase translation first, then AI translation
              const finalTranslation = phraseTranslation.translated || aiTranslation;
              
              // If phrase translation didn't work and we need AI translation, fetch it
              if (!isOwnMessage && !phraseTranslation.translated && phraseTranslation.needsAiTranslation && !aiTranslation) {
                fetchTranslation(msgKey, msg.message);
              }
              
              return (
                <div
                  key={msgKey}
                  className={`lobby-message ${isOwnMessage ? 'own-message' : ''}`}
                >
                  <div className="message-header">
                    <div className="message-author-wrapper">
                      <span className="message-author">{msg.user_name}</span>
                      {isOnline && <span className="message-online-dot" title="Online"></span>}
                    </div>
                    <span className="message-time">{formatTime(msg.timestamp)}</span>
                  </div>
                  <div className="message-content">{sanitizeMessageDisplay(msg.message)}</div>
                  {finalTranslation && finalTranslation.toLowerCase() !== msg.message.toLowerCase() && (
                    <div className="message-translation">
                      <span className="translation-label">🌐</span>
                      <span className="translated-text">{finalTranslation}</span>
                    </div>
                  )}
                  {!finalTranslation && phraseTranslation.sourceLang && phraseTranslation.sourceLang !== language && (
                    <div className="message-translation loading">
                      <span className="translation-label">🌐</span>
                      <span className="translated-text lang-indicator">Translating...</span>
                    </div>
                  )}
                </div>
              );
            })
          )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form className="lobby-input-form" onSubmit={handleSendMessage}>
          <input
            type="text"
            className="lobby-input"
            placeholder="Type a message..."
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            disabled={sending}
            maxLength={500}
          />
          <button
            type="submit"
            className="send-button"
            disabled={!messageInput.trim() || sending || !wsConnected}
            title={!wsConnected ? 'Connecting to chat...' : 'Send message'}
          >
            {!wsConnected ? 'Connecting...' : sending ? '...' : 'Send'}
          </button>
        </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LobbyChat;
